import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useStore } from '../context/StoreContext.tsx';
import { useAuth } from '../context/AuthContext.tsx';
import { Asset, AssetType, AssetStatus, Campaign } from '../types.ts';
import { 
  Search, Folder, Grid, List, Lock, CheckCircle, 
  Trash2, Edit2, Plus, Video, Image as ImageIcon,
  Layers, Maximize2, Loader2, MessageSquare, ChevronRight, ChevronDown, FolderOpen,
  MoreVertical, Download, Upload, X, Tag, FilePlus, ExternalLink,
  FolderPlus
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { AssetModal, DeleteConfirmationModal, formatDate, STATUS_CONFIG } from './AssetModal.tsx';

// --- OVERLAY MENU COMPONENT ---
const ActionOverlay: React.FC<{ 
  anchorEl: HTMLElement | null; 
  onClose: () => void; 
  children: React.ReactNode;
}> = ({ anchorEl, onClose, children }) => {
  if (!anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();
  // Ensure the menu doesn't overflow the bottom of the viewport
  const spaceBelow = window.innerHeight - rect.bottom;
  const menuHeight = 160; // Approximate
  const top = spaceBelow < menuHeight ? rect.top - menuHeight - 8 : rect.bottom + 8;
  
  const style: React.CSSProperties = {
    position: 'fixed',
    top: `${top}px`,
    right: `${window.innerWidth - rect.right}px`,
    zIndex: 1000,
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[999]" onClick={onClose} />
      <div style={style} className="w-44 bg-[var(--bg-primary)] rounded-xl shadow-2xl border border-[var(--border-subtle)] py-1.5 animate-fadeIn">
        {children}
      </div>
    </>,
    document.body
  );
};

// --- SIDEBAR COMPONENT ---
interface FolderNode {
  id: string;
  name: string;
  count: number;
  type: 'campaign' | 'system';
  parentId?: string;
  children: FolderNode[];
}

interface SidebarProps {
  systemFolders: FolderNode[];
  campaignTree: FolderNode[];
  selectedFolderId: string;
  setSelectedFolderId: (id: string) => void;
  filterType: string;
  setFilterType: (type: any) => void;
  filterStatus: string;
  setFilterStatus: (status: any) => void;
  isCreatingFolder: boolean;
  setIsCreatingFolder: (v: boolean) => void;
  createSubfolderFor: string | null;
  setCreateSubfolderFor: (id: string | null) => void;
  newFolderName: string;
  setNewFolderName: (v: string) => void;
  onCreateFolder: (e: React.FormEvent, parentId?: string) => void;
  editingFolderId: string | null;
  setEditingFolderId: (id: string | null) => void;
  editFolderName: string;
  setEditFolderName: (v: string) => void;
  onRenameFolder: (e: React.FormEvent) => void;
  onRequestDeleteFolder: (id: string, name: string) => void;
  onMoveFolder: (draggedId: string, targetId: string) => Promise<void>;
}

const FolderItem: React.FC<{
  node: FolderNode;
  selectedFolderId: string;
  setSelectedFolderId: (id: string) => void;
  onCreateSubfolder: (parentId: string) => void;
  isCreatingSubfolder: boolean;
  newFolderName: string;
  setNewFolderName: (v: string) => void;
  onSubmitCreate: (e: React.FormEvent, parentId: string) => void;
  editingFolderId: string | null;
  setEditingFolderId: (id: string | null) => void;
  editFolderName: string;
  setEditFolderName: (v: string) => void;
  onRenameFolder: (e: React.FormEvent) => void;
  onRequestDeleteFolder: (id: string, name: string) => void;
  onMoveFolder: (draggedId: string, targetId: string) => Promise<void>;
  level?: number;
}> = ({ 
  node, selectedFolderId, setSelectedFolderId, 
  onCreateSubfolder, isCreatingSubfolder, newFolderName, setNewFolderName, onSubmitCreate,
  editingFolderId, setEditingFolderId, editFolderName, setEditFolderName, onRenameFolder, onRequestDeleteFolder, onMoveFolder,
  level = 0
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const isSelected = selectedFolderId === node.id;
  const isEditing = editingFolderId === node.id;
  const hasChildren = node.children.length > 0;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (isEditing) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    e.dataTransfer.setData('folderId', node.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const draggedId = e.dataTransfer.getData('folderId');
    if (draggedId && draggedId !== node.id) {
      onMoveFolder(draggedId, node.id);
      setIsExpanded(true);
    }
  };

  const Icon = isSelected || (hasChildren && isExpanded) ? FolderOpen : Folder;

  return (
    <div className="select-none">
      <div 
        draggable={!isEditing}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`group flex items-center justify-between py-2 pr-2 rounded-lg text-sm transition-colors cursor-pointer relative border border-transparent
          ${isSelected 
            ? 'bg-[var(--bg-primary)] text-[var(--text-primary)] shadow-sm border-[var(--border-subtle)]' 
            : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
          }
          ${isDragOver ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 !text-blue-600 dark:!text-blue-300 z-10' : ''}
        `}
        onClick={() => setSelectedFolderId(node.id)}
      >
        <div className="flex-1 flex items-center gap-2 overflow-hidden mr-2" style={{ paddingLeft: `${Math.max(12, level * 16 + 12)}px` }}>
          <button onClick={handleToggle} className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-tertiary)] transition-colors ${!hasChildren ? 'invisible' : ''}`}>
             {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
          <Icon size={16} className={`flex-shrink-0 ${isSelected ? 'text-[var(--text-primary)] fill-[var(--text-primary)]/10' : 'text-[var(--text-tertiary)]'}`} />
          {isEditing ? (
             <form onSubmit={onRenameFolder} onClick={e => e.stopPropagation()} className="flex-1 min-w-0">
               <input autoFocus className="input-field text-xs py-1 px-2 h-7 w-full" value={editFolderName} onChange={(e) => setEditFolderName(e.target.value)} onBlur={() => setEditingFolderId(null)} />
             </form>
          ) : (
            <span className={`truncate text-sm ${isSelected ? 'font-medium' : ''}`}>{node.name}</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {node.type === 'campaign' && !isEditing && (
            <div className="hidden group-hover:flex gap-0.5">
                <button type="button" onClick={(e) => { e.stopPropagation(); onCreateSubfolder(node.id); setIsExpanded(true); }} className="p-1 hover:bg-[var(--bg-tertiary)] rounded text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><Plus size={12} /></button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setEditingFolderId(node.id); setEditFolderName(node.name); }} className="p-1 hover:bg-[var(--bg-tertiary)] rounded text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><Edit2 size={12} /></button>
                <button type="button" onClick={(e) => { e.stopPropagation(); onRequestDeleteFolder(node.id, node.name); }} className="p-1 hover:bg-red-100 rounded text-[var(--text-secondary)] hover:text-red-500"><Trash2 size={12} /></button>
            </div>
          )}
          {node.count > 0 && (
            <span className={`text-[10px] h-5 min-w-[20px] px-1.5 flex items-center justify-center rounded-full ${isSelected ? 'bg-[var(--text-primary)] text-[var(--bg-primary)]' : 'bg-[var(--bg-tertiary)] text-[var(--text-tertiary)]'}`}>
              {node.count}
            </span>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="relative">
           <div className="absolute top-0 bottom-0 border-l border-[var(--border-subtle)]" style={{ left: `${(level * 16) + 20}px`, opacity: 0.5 }}></div>
           {isCreatingSubfolder && (
              <div className="py-1 pr-2 mb-1" style={{ paddingLeft: `${(level + 1) * 16 + 24}px` }}>
                 <form onSubmit={(e) => onSubmitCreate(e, node.id)} className="flex items-center gap-2">
                   <input autoFocus className="input-field text-xs py-1 px-2 h-7 w-full" placeholder="New Subfolder" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} onBlur={() => !newFolderName && onCreateSubfolder('')} />
                 </form>
              </div>
           )}
           {node.children.map(child => (
             <FolderItem 
               key={child.id} node={child} selectedFolderId={selectedFolderId} setSelectedFolderId={setSelectedFolderId}
               onCreateSubfolder={onCreateSubfolder} isCreatingSubfolder={false} newFolderName={newFolderName} setNewFolderName={setNewFolderName} onSubmitCreate={onSubmitCreate}
               editingFolderId={editingFolderId} setEditingFolderId={setEditingFolderId} editFolderName={editFolderName} setEditFolderName={setEditFolderName} onRenameFolder={onRenameFolder} onRequestDeleteFolder={onRequestDeleteFolder} onMoveFolder={onMoveFolder}
               level={level + 1}
             />
           ))}
        </div>
      )}
    </div>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  systemFolders, campaignTree, selectedFolderId, setSelectedFolderId,
  filterType, setFilterType,
  filterStatus, setFilterStatus,
  isCreatingFolder, setIsCreatingFolder, createSubfolderFor, setCreateSubfolderFor,
  newFolderName, setNewFolderName, onCreateFolder,
  editingFolderId, setEditingFolderId, editFolderName, setEditFolderName, onRenameFolder, onRequestDeleteFolder,
  onMoveFolder
}) => {
  return (
    <div className="w-72 bg-[var(--bg-secondary)] border-r border-[var(--border-subtle)] flex flex-col h-full flex-shrink-0">
      <div className="p-5 border-b border-[var(--border-subtle)]">
        <h2 className="text-2xl font-heading font-bold text-[var(--text-primary)]">Asset Library</h2>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-6 scrollbar-hide">
        <div>
          <div className="flex items-center justify-between mb-2 px-2">
            <h3 className="text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider">Folders</h3>
            <button type="button" onClick={() => setIsCreatingFolder(true)} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1 rounded hover:bg-[var(--bg-tertiary)]"><Plus size={14} /></button>
          </div>
          <div className="space-y-0.5">
            {systemFolders.map(folder => (
              <div key={folder.id} className={`w-full flex items-center justify-between pl-3 pr-2 py-2 rounded-lg text-sm transition-colors cursor-pointer border border-transparent ${selectedFolderId === folder.id ? 'bg-[var(--bg-primary)] text-[var(--text-primary)] shadow-sm border-[var(--border-subtle)]' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'}`} onClick={() => setSelectedFolderId(folder.id)}>
                 <div className="flex items-center gap-2">
                   <Folder size={16} className={`flex-shrink-0 ${selectedFolderId === folder.id ? 'text-[var(--text-primary)] fill-[var(--text-primary)]/10' : 'text-[var(--text-tertiary)]'}`} />
                   <span className={selectedFolderId === folder.id ? 'font-medium' : ''}>{folder.name}</span>
                 </div>
                 {folder.count > 0 && (
                   <span className={`text-[10px] h-5 min-w-[20px] px-1.5 flex items-center justify-center rounded-full ${selectedFolderId === folder.id ? 'bg-[var(--text-primary)] text-[var(--bg-primary)]' : 'bg-[var(--bg-tertiary)] text-[var(--text-tertiary)]'}`}>{folder.count}</span>
                 )}
              </div>
            ))}
            <div className="my-3 h-px bg-[var(--border-subtle)] mx-2" />
            {isCreatingFolder && (
              <div className="px-1 py-1 mb-1">
                 <form onSubmit={(e) => onCreateFolder(e)}><input autoFocus className="input-field text-xs py-1.5 px-3" placeholder="New Folder Name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} onBlur={() => !newFolderName && setIsCreatingFolder(false)} /></form>
              </div>
            )}
            {campaignTree.map(node => (
                <FolderItem key={node.id} node={node} selectedFolderId={selectedFolderId} setSelectedFolderId={setSelectedFolderId} onCreateSubfolder={(id) => setCreateSubfolderFor(id)} isCreatingSubfolder={createSubfolderFor === node.id} newFolderName={newFolderName} setNewFolderName={setNewFolderName} onSubmitCreate={onCreateFolder} editingFolderId={editingFolderId} setEditingFolderId={setEditingFolderId} editFolderName={editFolderName} setEditFolderName={setEditFolderName} onRenameFolder={onRenameFolder} onRequestDeleteFolder={onRequestDeleteFolder} onMoveFolder={onMoveFolder} />
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-2 px-2">Asset Type</h3>
          <div className="space-y-1 px-2">
            {[ { id: 'all', label: 'All Types', icon: Layers }, { id: AssetType.Image, label: 'Images', icon: ImageIcon }, { id: AssetType.Video, label: 'Videos', icon: Video }, { id: AssetType.Caption, label: 'Captions', icon: MessageSquare } ].map(type => (
              <label key={type.id} className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] cursor-pointer py-1.5 px-2 rounded-lg hover:bg-[var(--bg-tertiary)]">
                <input type="radio" name="assetType" checked={filterType === type.id} onChange={() => setFilterType(type.id)} className="accent-[var(--text-primary)]" />
                <type.icon size={14} /> {type.label}
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterBar: React.FC<{
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  selectedCount: number;
  onBulkAction: (action: 'delete' | 'deploy') => void;
  isBulkProcessing: boolean;
  viewMode: 'grid' | 'list';
  setViewMode: (m: 'grid' | 'list') => void;
  sortOrder: string;
  setSortOrder: (s: any) => void;
  onUpload: () => void;
}> = ({
  searchQuery, setSearchQuery, selectedCount, onBulkAction, isBulkProcessing, viewMode, setViewMode, sortOrder, setSortOrder, onUpload
}) => (
  <div className="h-16 border-b border-[var(--border-subtle)] bg-[var(--bg-primary)] flex items-center justify-between px-6 flex-shrink-0 z-10 sticky top-0 gap-4">
     <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="relative flex-1 max-w-lg transition-all overflow-hidden">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" size={16} />
          <input type="text" placeholder="Search assets..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-[var(--bg-secondary)] border-none rounded-lg text-sm focus:ring-1 ring-[var(--border-default)] outline-none" />
        </div>
        <button 
          onClick={onUpload}
          className="btn-black py-2 px-4 text-sm flex items-center gap-2 flex-shrink-0"
        >
          <Upload size={16} /> <span className="hidden sm:inline">Upload Asset</span>
        </button>
     </div>
     <div className="flex items-center gap-3 flex-shrink-0">
        {selectedCount > 0 && (
           <div className="flex items-center gap-2 mr-2 bg-red-50 dark:bg-red-900/10 px-3 py-1.5 rounded-lg border border-red-100 dark:border-red-900/30">
              <span className="text-xs font-bold text-red-600 dark:text-red-400">{selectedCount}</span>
              <button type="button" onClick={() => onBulkAction('delete')} disabled={isBulkProcessing} className="p-1 text-red-500 hover:bg-red-100 rounded transition-colors">{isBulkProcessing ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}</button>
           </div>
        )}
        <div className="hidden sm:flex items-center gap-1 bg-[var(--bg-secondary)] p-1 rounded-lg border border-[var(--border-subtle)]">
           <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-[var(--bg-primary)] shadow-sm' : 'text-[var(--text-tertiary)]'}`} title="Grid View"><Grid size={16} /></button>
           <button onClick={() => setViewMode('list')} className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-[var(--bg-primary)] shadow-sm' : 'text-[var(--text-tertiary)]'}`} title="List View"><List size={16} /></button>
        </div>
        <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as any)} className="input-field py-2 text-sm w-auto min-w-[120px] border-[var(--border-subtle)] bg-transparent">
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="name">Name (A-Z)</option>
        </select>
     </div>
  </div>
);

// --- MAIN COMPONENT ---

const AssetLibrary: React.FC = () => {
  const { assets, campaigns, updateAsset, deleteAsset, deleteAssets, addCampaign, updateCampaign, deleteCampaign, addAsset, addNotification } = useStore();
  const { user } = useAuth();
  const [selectedFolderId, setSelectedFolderId] = useState<string>('all');
  const [filterType, setFilterType] = useState<any>('all');
  const [filterStatus, setFilterStatus] = useState<any>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [createSubfolderFor, setCreateSubfolderFor] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortOrder, setSortOrder] = useState<any>('newest');
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  
  // Overlay menu state
  const [activeMenuAssetId, setActiveMenuAssetId] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  // Manual Upload Modal State
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isCreatingCampaignInUpload, setIsCreatingCampaignInUpload] = useState(false);
  const [newCampaignNameInUpload, setNewCampaignNameInUpload] = useState('');
  const [uploadData, setUploadData] = useState({
    file: null as File | null,
    base64: null as string | null,
    scenario: '',
    campaignId: '',
    tags: ''
  });
  const [isUploading, setIsUploading] = useState(false);

  // Delete Confirmation Modal state
  const [deleteConfirm, setDeleteConfirm] = useState<{isOpen: boolean; type: 'single' | 'bulk' | 'folder'; ids: string[]; title: string; message: React.ReactNode;}>({ isOpen: false, type: 'single', ids: [], title: '', message: '' });
  const [isProcessingDelete, setIsProcessingDelete] = useState(false);

  const { systemFolders, campaignTree } = useMemo(() => {
     const system: FolderNode[] = [
       { id: 'all', name: 'All Assets', count: assets.length, type: 'system', children: [] },
       { id: 'unassigned', name: 'Unassigned', count: assets.filter(a => !a.campaignId).length, type: 'system', children: [] },
     ];
     const nodeMap = new Map<string, FolderNode>();
     campaigns.forEach(c => nodeMap.set(c.id, { id: c.id, name: c.name, count: assets.filter(a => a.campaignId === c.id).length, type: 'campaign', parentId: c.parentId, children: [] }));
     const roots: FolderNode[] = [];
     campaigns.forEach(c => {
        const node = nodeMap.get(c.id);
        if (node) {
          if (c.parentId && nodeMap.has(c.parentId)) nodeMap.get(c.parentId)!.children.push(node);
          else roots.push(node);
        }
     });
     return { systemFolders: system, campaignTree: roots };
  }, [assets, campaigns]);

  const filteredAssets = useMemo(() => {
    return assets.filter(asset => {
      if (selectedFolderId === 'unassigned') { if (asset.campaignId) return false; }
      else if (selectedFolderId !== 'all') { if (asset.campaignId !== selectedFolderId) return false; }
      if (filterType !== 'all' && asset.type !== filterType) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return asset.scenario.toLowerCase().includes(q) || asset.tags.some(t => t.toLowerCase().includes(q));
      }
      return true;
    }).sort((a, b) => {
      if (sortOrder === 'newest') return b.createdAt - a.createdAt;
      if (sortOrder === 'oldest') return a.createdAt - b.createdAt;
      if (sortOrder === 'name') return a.scenario.localeCompare(b.scenario);
      return 0;
    });
  }, [assets, selectedFolderId, filterType, searchQuery, sortOrder]);

  const activeAsset = useMemo(() => assets.find(a => a.id === selectedAssetId), [assets, selectedAssetId]);

  const handleSelectAsset = (id: string, multi: boolean) => {
    if (multi) {
      const newSet = new Set(selectedAssetIds);
      if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
      setSelectedAssetIds(newSet);
    } else setSelectedAssetId(id);
  };

  const confirmDelete = async () => {
    setIsProcessingDelete(true);
    try {
      if (deleteConfirm.type === 'single') await deleteAsset(deleteConfirm.ids[0]);
      else if (deleteConfirm.type === 'bulk') await deleteAssets(deleteConfirm.ids);
      else if (deleteConfirm.type === 'folder') await deleteCampaign(deleteConfirm.ids[0]);
      setDeleteConfirm(prev => ({ ...prev, isOpen: false }));
      setSelectedAssetIds(new Set());
    } finally { setIsProcessingDelete(false); }
  };

  const handleBulkAction = async (action: 'delete' | 'deploy') => {
    if (action === 'delete') setDeleteConfirm({ isOpen: true, type: 'bulk', ids: Array.from(selectedAssetIds), title: 'Delete Selected?', message: 'Permanent action.' });
    else { await Promise.all(Array.from(selectedAssetIds).map(id => updateAsset(id, { status: AssetStatus.Deployed }))); setSelectedAssetIds(new Set()); }
  };

  const handleCreateFolder = async (e: React.FormEvent, parentId?: string) => {
    e.preventDefault(); if (!newFolderName.trim()) return;
    await addCampaign({ id: `camp-${Date.now()}`, name: newFolderName.trim(), description: '', startDate: new Date().toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0], status: 'active', assetCount: 0, parentId });
    setNewFolderName(''); setIsCreatingFolder(false); setCreateSubfolderFor(null);
  };

  const handleRenameFolder = async (e: React.FormEvent) => {
    e.preventDefault(); if (!editingFolderId || !editFolderName.trim()) return;
    await updateCampaign(editingFolderId, { name: editFolderName.trim() });
    setEditingFolderId(null);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadData(prev => ({ 
        ...prev, 
        file, 
        base64: ev.target?.result as string,
        scenario: prev.scenario || file.name.split('.')[0]
      }));
    };
    reader.readAsDataURL(file);
  };

  const confirmUpload = async () => {
    if (!uploadData.base64 || !uploadData.scenario || isUploading) return;
    setIsUploading(true);
    try {
      let campaignIdToUse = uploadData.campaignId;

      // Handle on-the-fly campaign creation
      if (isCreatingCampaignInUpload && newCampaignNameInUpload.trim()) {
        const newId = `camp-${Date.now()}`;
        await addCampaign({
          id: newId,
          name: newCampaignNameInUpload.trim(),
          description: 'Manually created via upload',
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0],
          status: 'active',
          assetCount: 0
        });
        campaignIdToUse = newId;
      }

      const isVideo = uploadData.file?.type.startsWith('video/');
      const asset = {
        id: `man-${Date.now()}`,
        url: uploadData.base64,
        type: isVideo ? AssetType.Video : AssetType.Image,
        createdAt: Date.now(),
        scenario: uploadData.scenario,
        status: AssetStatus.Available,
        campaignId: campaignIdToUse || undefined,
        tags: uploadData.tags.split(',').map(t => t.trim()).filter(Boolean),
        meta: { manual: true, originalName: uploadData.file?.name }
      };
      
      await addAsset(asset);
      addNotification('success', 'Asset Uploaded', `Manually added ${uploadData.scenario} to library.`);
      setIsUploadModalOpen(false);
      setUploadData({ file: null, base64: null, scenario: '', campaignId: '', tags: '' });
      setIsCreatingCampaignInUpload(false);
      setNewCampaignNameInUpload('');
    } catch (e) {
      console.error("Manual upload failed:", e);
      addNotification('error', 'Upload Failed', 'Could not save asset.');
    } finally {
      setIsUploading(false);
    }
  };

  const getCreatorDisplay = (asset: Asset) => ({ name: user?.uid === asset.createdBy ? 'You' : asset.createdByName || 'Unknown', avatar: asset.createdByAvatar });

  return (
    <div className="flex h-[calc(100vh-80px)] overflow-hidden">
      <Sidebar systemFolders={systemFolders} campaignTree={campaignTree} selectedFolderId={selectedFolderId} setSelectedFolderId={setSelectedFolderId} filterType={filterType} setFilterType={setFilterType} filterStatus={filterStatus} setFilterStatus={setFilterStatus} isCreatingFolder={isCreatingFolder} setIsCreatingFolder={setIsCreatingFolder} createSubfolderFor={createSubfolderFor} setCreateSubfolderFor={setCreateSubfolderFor} newFolderName={newFolderName} setNewFolderName={setNewFolderName} onCreateFolder={handleCreateFolder} editingFolderId={editingFolderId} setEditingFolderId={setEditingFolderId} editFolderName={editFolderName} setEditFolderName={setEditFolderName} onRenameFolder={handleRenameFolder} onRequestDeleteFolder={(id, n) => setDeleteConfirm({isOpen: true, type: 'folder', ids: [id], title: `Delete ${n}?`, message: 'Assets will be unassigned.'})} onMoveFolder={async (d, t) => await updateCampaign(d, { parentId: t === 'all' ? undefined : t })} />
      
      <div className="flex-1 flex flex-col min-w-0 bg-[var(--bg-primary)]">
        <FilterBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} selectedCount={selectedAssetIds.size} onBulkAction={handleBulkAction} isBulkProcessing={isProcessingDelete} viewMode={viewMode} setViewMode={setViewMode} sortOrder={sortOrder} setSortOrder={setSortOrder} onUpload={() => setIsUploadModalOpen(true)} />
        
        <div className="flex-1 overflow-y-auto p-6 bg-[var(--bg-tertiary)]/30">
           {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 pb-20">
                 {filteredAssets.map(asset => {
                   const { name, avatar } = getCreatorDisplay(asset);
                   const isSelected = selectedAssetIds.has(asset.id);

                   return (
                    <div key={asset.id} className={`group relative card overflow-hidden hover:shadow-lg transition-all border-[var(--border-subtle)] ${isSelected ? 'ring-2 ring-[var(--accent-primary)] border-transparent' : ''}`}>
                      <div className={`absolute top-2 left-2 z-10 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}><input type="checkbox" checked={isSelected} onChange={(e) => { e.stopPropagation(); handleSelectAsset(asset.id, true); }} className="w-5 h-5 rounded accent-[var(--accent-primary)] shadow-sm" /></div>
                      
                      {/* Menu Trigger */}
                      <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                         <button 
                           onClick={(e) => { e.stopPropagation(); setMenuAnchorEl(e.currentTarget); setActiveMenuAssetId(asset.id); }}
                           className="p-1.5 rounded-lg bg-white/90 backdrop-blur shadow-sm border border-gray-200 text-gray-700 hover:text-black transition-colors"
                         >
                           <MoreVertical size={16} />
                         </button>
                      </div>

                      <div className="aspect-square bg-[var(--bg-tertiary)] relative overflow-hidden cursor-pointer" onClick={() => handleSelectAsset(asset.id, false)}>
                        {asset.type === AssetType.Image && <img src={asset.url} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                        {asset.type === AssetType.Video && <div className="w-full h-full flex items-center justify-center bg-gray-900 text-white transition-transform duration-500 group-hover:scale-105"><Video size={48} /></div>}
                        {asset.type === AssetType.Caption && <div className="w-full h-full p-4 flex flex-col justify-center bg-[var(--bg-secondary)]"><p className="text-xs line-clamp-5 font-mono leading-relaxed">{asset.url}</p></div>}
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center pointer-events-none transition-opacity duration-300">
                           <span className="px-3 py-1.5 bg-white/90 backdrop-blur rounded-full text-xs font-bold text-black shadow-lg flex items-center gap-1.5">
                              <Maximize2 size={12} /> View
                           </span>
                        </div>
                      </div>
                      <div className="p-3 bg-[var(--bg-primary)]">
                        <h3 className="text-sm font-bold text-[var(--text-primary)] truncate">{asset.scenario}</h3>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1.5 overflow-hidden">
                            {avatar ? <img src={avatar} className="w-5 h-5 rounded-full object-cover border border-gray-200" /> : <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-[10px]">{name[0]}</div>}
                            <span className="text-[10px] text-[var(--text-tertiary)] truncate">{name}</span>
                          </div>
                          <span className="text-[10px] text-[var(--text-tertiary)] font-medium">{formatDate(asset.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                   );
                 })}
                 
                 {filteredAssets.length === 0 && (
                   <div className="col-span-full py-32 flex flex-col items-center justify-center text-[var(--text-tertiary)] opacity-60">
                      <Folder size={48} className="mb-4" />
                      <p className="text-lg font-medium">No assets found in this folder</p>
                      <p className="text-sm">Try changing your filters or searching for something else.</p>
                   </div>
                 )}
              </div>
           ) : (
              <div className="bg-[var(--bg-primary)] rounded-xl border border-[var(--border-subtle)] overflow-hidden shadow-sm pb-20">
                 <table className="w-full text-left">
                   <thead className="bg-[var(--bg-secondary)] border-b border-[var(--border-subtle)]">
                     <tr>
                       <th className="p-4 w-10"></th>
                       <th className="p-4 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider">Asset</th>
                       <th className="p-4 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider text-center">Type</th>
                       <th className="p-4 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider text-center">Status</th>
                       <th className="p-4 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider">Date</th>
                       <th className="p-4 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider text-right">Actions</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-[var(--border-subtle)]">
                      {filteredAssets.map(asset => {
                        const isSelected = selectedAssetIds.has(asset.id);
                        const status = STATUS_CONFIG[asset.status] || STATUS_CONFIG[AssetStatus.Available];
                        const StatusIcon = status.icon;

                        return (
                          <tr key={asset.id} className={`hover:bg-[var(--bg-secondary)] transition-colors group ${isSelected ? 'bg-blue-50/30' : ''}`}>
                             <td className="p-4">
                                <input type="checkbox" checked={isSelected} onChange={() => handleSelectAsset(asset.id, true)} className="w-4 h-4 rounded accent-[var(--accent-primary)] shadow-sm" />
                             </td>
                             <td className="p-4">
                               <div className="flex items-center gap-3 cursor-pointer" onClick={() => setSelectedAssetId(asset.id)}>
                                 <div className="w-10 h-10 rounded border border-[var(--border-subtle)] bg-[var(--bg-tertiary)] overflow-hidden flex-shrink-0">
                                   {asset.type === AssetType.Image && <img src={asset.url} className="w-full h-full object-cover" />}
                                   {asset.type === AssetType.Video && <div className="w-full h-full flex items-center justify-center bg-gray-900 text-white"><Video size={16} /></div>}
                                   {asset.type === AssetType.Caption && <div className="w-full h-full p-1"><MessageSquare size={16} className="text-gray-400" /></div>}
                                 </div>
                                 <div className="min-w-0">
                                   <div className="text-sm font-bold text-[var(--text-primary)] truncate">{asset.scenario}</div>
                                   <div className="text-[10px] text-[var(--text-tertiary)] truncate">{asset.tags.join(', ')}</div>
                                 </div>
                               </div>
                             </td>
                             <td className="p-4 text-center">
                               <span className="text-[10px] font-bold text-[var(--text-tertiary)] uppercase tracking-widest">{asset.type}</span>
                             </td>
                             <td className="p-4 text-center">
                                <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold ${status.color}`}>
                                   <StatusIcon size={12} /> {status.label}
                                </div>
                             </td>
                             <td className="p-4 text-xs text-[var(--text-secondary)] whitespace-nowrap">
                               {formatDate(asset.createdAt)}
                             </td>
                             <td className="p-4 text-right">
                               <button 
                                 onClick={(e) => { e.stopPropagation(); setMenuAnchorEl(e.currentTarget); setActiveMenuAssetId(asset.id); }}
                                 className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
                               >
                                  <MoreVertical size={16} />
                               </button>
                             </td>
                          </tr>
                        );
                      })}
                   </tbody>
                 </table>
              </div>
           )}
        </div>
      </div>

      {/* Manual Upload Modal */}
      {isUploadModalOpen && createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
           <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-xl border border-[var(--border-subtle)] animate-slideUp overflow-hidden">
              <div className="flex items-center justify-between p-5 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                 <h3 className="font-bold text-lg text-[var(--text-primary)] flex items-center gap-2">
                    <FilePlus size={20} className="text-[var(--accent-primary)]" />
                    Upload Local Asset
                 </h3>
                 <button onClick={() => setIsUploadModalOpen(false)} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1 rounded-full hover:bg-[var(--bg-tertiary)]">
                   <X size={20} />
                 </button>
              </div>
              
              <div className="p-6 space-y-6">
                 {!uploadData.base64 ? (
                   <label className="block w-full border-2 border-dashed border-[var(--border-default)] rounded-xl p-12 text-center cursor-pointer hover:border-[var(--accent-primary)] hover:bg-[var(--bg-secondary)] transition-all group">
                      <div className="w-16 h-16 bg-[var(--bg-tertiary)] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                         <Upload className="text-[var(--text-tertiary)] group-hover:text-[var(--accent-primary)]" size={32} />
                      </div>
                      <span className="text-sm font-bold text-[var(--text-primary)]">Click to choose or drag-and-drop</span>
                      <p className="text-xs text-[var(--text-tertiary)] mt-1">Images or MP4 Videos supported</p>
                      <input type="file" className="hidden" accept="image/*,video/mp4" onChange={handleFileUpload} />
                   </label>
                 ) : (
                   <div className="relative aspect-video rounded-xl overflow-hidden border border-[var(--border-subtle)] bg-[var(--bg-tertiary)] flex items-center justify-center">
                      {uploadData.file?.type.startsWith('video/') ? (
                         <video src={uploadData.base64} className="max-h-full" controls />
                      ) : (
                        <img src={uploadData.base64} className="max-h-full object-contain" />
                      )}
                      <button onClick={() => setUploadData(prev => ({ ...prev, file: null, base64: null }))} className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-red-500 transition-colors">
                        <X size={16} />
                      </button>
                   </div>
                 )}

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                       <label className="block text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">Title / Scenario</label>
                       <input 
                         type="text" 
                         className="input-field" 
                         placeholder="e.g. Chip at 2024 Trade Show"
                         value={uploadData.scenario}
                         onChange={(e) => setUploadData(prev => ({ ...prev, scenario: e.target.value }))}
                       />
                    </div>
                    <div>
                       <label className="block text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">Campaign (Folder)</label>
                       {!isCreatingCampaignInUpload ? (
                         <div className="space-y-2">
                           <select className="input-field" value={uploadData.campaignId} onChange={(e) => setUploadData(prev => ({ ...prev, campaignId: e.target.value }))}>
                             <option value="">Unassigned</option>
                             {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                           </select>
                           <button 
                             onClick={() => setIsCreatingCampaignInUpload(true)}
                             className="text-[10px] text-[var(--accent-primary)] font-bold uppercase flex items-center gap-1 hover:underline"
                           >
                             <Plus size={10} /> Create New Campaign
                           </button>
                         </div>
                       ) : (
                         <div className="space-y-2">
                           <input 
                             type="text" 
                             className="input-field" 
                             placeholder="New Campaign Name" 
                             autoFocus
                             value={newCampaignNameInUpload}
                             onChange={(e) => setNewCampaignNameInUpload(e.target.value)}
                           />
                           <button 
                             onClick={() => setIsCreatingCampaignInUpload(false)}
                             className="text-[10px] text-[var(--text-tertiary)] font-bold uppercase hover:underline"
                           >
                             Cancel
                           </button>
                         </div>
                       )}
                    </div>
                    <div>
                       <label className="block text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">Tags</label>
                       <input type="text" className="input-field" placeholder="event, 2024" value={uploadData.tags} onChange={(e) => setUploadData(prev => ({ ...prev, tags: e.target.value }))} />
                    </div>
                 </div>

                 <div className="flex justify-end gap-3 pt-6 border-t border-[var(--border-subtle)]">
                    <button onClick={() => setIsUploadModalOpen(false)} className="px-6 py-2.5 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] rounded-lg">Cancel</button>
                    <button 
                      onClick={confirmUpload} 
                      disabled={!uploadData.base64 || !uploadData.scenario || isUploading} 
                      className="btn-black flex items-center gap-2 px-8 min-w-[140px] justify-center disabled:opacity-50"
                    >
                      {isUploading ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
                      {isUploading ? 'Saving...' : 'Save Asset'}
                    </button>
                 </div>
              </div>
           </div>
        </div>,
        document.body
      )}

      {/* Overlay Action Menu */}
      <ActionOverlay anchorEl={menuAnchorEl} onClose={() => { setMenuAnchorEl(null); setActiveMenuAssetId(null); }}>
          <button onClick={() => { handleSelectAsset(activeMenuAssetId!, false); setMenuAnchorEl(null); }} className="w-full text-left px-4 py-2 text-xs font-semibold hover:bg-[var(--bg-secondary)] flex items-center gap-2.5 transition-colors"><Maximize2 size={14} className="text-[var(--text-tertiary)]" /> View Details</button>
          <button onClick={() => { setMenuAnchorEl(null); }} className="w-full text-left px-4 py-2 text-xs font-semibold hover:bg-[var(--bg-secondary)] flex items-center gap-2.5 transition-colors"><Download size={14} className="text-[var(--text-tertiary)]" /> Download</button>
          <button onClick={() => { setMenuAnchorEl(null); }} className="w-full text-left px-4 py-2 text-xs font-semibold hover:bg-[var(--bg-secondary)] flex items-center gap-2.5 transition-colors"><Folder size={14} className="text-[var(--text-tertiary)]" /> Move to Folder</button>
          <div className="h-px bg-[var(--border-subtle)] my-1"></div>
          <button onClick={() => { setDeleteConfirm({ isOpen: true, type: 'single', ids: [activeMenuAssetId!], title: 'Delete Asset?', message: 'This action is permanent.' }); setMenuAnchorEl(null); }} className="w-full text-left px-4 py-2 text-xs font-semibold text-red-500 hover:bg-red-50 flex items-center gap-2.5 transition-colors"><Trash2 size={14} /> Delete</button>
      </ActionOverlay>

      {activeAsset && <AssetModal asset={activeAsset} onClose={() => setSelectedAssetId(null)} onUpdate={updateAsset} onRequestDelete={(id) => setDeleteConfirm({isOpen: true, type: 'single', ids: [id], title: 'Delete?', message: 'Permanent.'})} campaigns={campaigns} creatorInfo={getCreatorDisplay(activeAsset)} />}
      <DeleteConfirmationModal isOpen={deleteConfirm.isOpen} title={deleteConfirm.title} message={deleteConfirm.message} onConfirm={confirmDelete} onCancel={() => setDeleteConfirm(prev => ({ ...prev, isOpen: false }))} isProcessing={isProcessingDelete} />
    </div>
  );
};

export default AssetLibrary;